<ng-container *transloco="let t">
  <div
    id="reset-password-card"
    class="bg-white rounded-2xl max-sm:rounded-b-none shadow-card max-sm:shadow-none w-full max-w-[480px] max-sm:max-w-full min-w-0 p-4 sm:p-10 max-xs:px-3"
  >
    <div id="reset-password-header" class="flex flex-col items-center gap-2 mb-4">
      <img id="reset-password-logo" [src]="logoUrl()" [alt]="partnerDisplayName()" class="h-14 w-auto" />
      <h1 id="reset-password-title" class="text-section-heading text-ld-neutral-700 m-0">
        {{ t('authResetPasswordTitle') }}
      </h1>
    </div>

    <p id="reset-password-subtitle" class="font-dm text-sm leading-5 text-ld-neutral-500 text-center m-0 mb-6">
      {{ t('authResetPasswordSubtitle') }}
    </p>

    @if (errorMessage()) {
      <div
        id="reset-password-error-message"
        class="bg-ld-error-light border border-ld-error rounded-lg px-4 py-3 mb-6 text-ld-error text-sm"
        role="alert"
      >
        {{ errorMessage() }}
      </div>
    }

    <form id="reset-password-form" [formGroup]="resetPasswordForm" (ngSubmit)="onSubmit()" class="flex flex-col gap-6">
      <div>
        <mat-form-field id="reset-password-new-field" appearance="outline" class="w-full">
          <mat-label>{{ t('authResetPasswordNewPassword') }}</mat-label>
          <input
            matInput
            id="reset-password-new-input"
            [type]="hideNewPassword() ? 'password' : 'text'"
            formControlName="newPassword"
            aria-required="true"
            (input)="onNewPasswordInput()"
          />
          <button
            type="button"
            matSuffix
            id="reset-password-new-toggle"
            (click)="toggleNewPasswordVisibility()"
            class="bg-transparent border-0 cursor-pointer p-0 pr-2 text-ld-neutral-500 flex items-center justify-center hover:text-ld-neutral"
            [attr.aria-label]="hideNewPassword() ? t('commonAriaShowPassword') : t('commonAriaHidePassword')"
          >
            <mat-icon>{{ hideNewPassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
        </mat-form-field>

        <ul id="reset-password-validation-list" class="list-none p-0 m-0 mt-2 flex flex-col gap-1">
          @for (rule of passwordValidationRules(); track rule.key) {
            <li
              class="flex items-center gap-2 font-dm text-xs leading-4"
              [class.text-green-600]="rule.valid"
              [class.text-ld-neutral-400]="!rule.valid"
            >
              <mat-icon class="text-base! w-4 h-4 leading-4">
                {{ rule.valid ? 'check_circle' : 'radio_button_unchecked' }}
              </mat-icon>
              <span>{{ t(rule.label) }}</span>
            </li>
          }
        </ul>
      </div>

      <div>
        <mat-form-field id="reset-password-verify-field" appearance="outline" class="w-full">
          <mat-label>{{ t('authResetPasswordVerifyPassword') }}</mat-label>
          <input
            matInput
            id="reset-password-verify-input"
            [type]="hideVerifyPassword() ? 'password' : 'text'"
            formControlName="verifyPassword"
            aria-required="true"
            (input)="onVerifyPasswordInput()"
          />
          <button
            type="button"
            matSuffix
            id="reset-password-verify-toggle"
            (click)="toggleVerifyPasswordVisibility()"
            class="bg-transparent border-0 cursor-pointer p-0 pr-2 text-ld-neutral-500 flex items-center justify-center hover:text-ld-neutral"
            [attr.aria-label]="hideVerifyPassword() ? t('commonAriaShowPassword') : t('commonAriaHidePassword')"
          >
            <mat-icon>{{ hideVerifyPassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
          @if (
            resetPasswordForm.get('verifyPassword')?.hasError('matchValue') &&
            resetPasswordForm.get('verifyPassword')?.touched
          ) {
            <mat-error id="reset-password-verify-error-match">{{ t('authResetPasswordValidationMatch') }}</mat-error>
          }
        </mat-form-field>

        @if (passwordsMatch()) {
          <div class="flex items-center gap-2 font-dm text-xs leading-4 text-green-600 mt-1">
            <mat-icon class="text-base! w-4 h-4 leading-4">check_circle</mat-icon>
            <span>{{ t('authResetPasswordValidationMatch') }}</span>
          </div>
        }
      </div>

      <button
        type="submit"
        id="reset-password-submit-button"
        class="btn-primary-ld-purple !order-none !w-full h-11 disabled:cursor-wait"
        [disabled]="loading()"
        [attr.aria-busy]="loading()"
      >
        @if (loading()) {
          <mat-spinner
            diameter="20"
            id="reset-password-spinner"
            class="text-white! [&_circle]:stroke-white!"
          ></mat-spinner>
        }
        <span>{{ t('authResetPasswordSubmit') }}</span>
      </button>

      <div id="reset-password-back-link" class="flex items-center justify-center">
        <button
          type="button"
          id="reset-password-back-button"
          (click)="onBackToLogin()"
          class="bg-transparent border-0 text-ld-link cursor-pointer p-0 font-dm text-sm font-medium leading-4 hover:underline focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 focus-visible:rounded touch-target"
        >
          {{ t('commonLabelsBackToSignIn') }}
        </button>
      </div>
    </form>
  </div>
</ng-container>
